import Backbone from "backbone";
import _ from "underscore";
import { Dialog, IDialogButton } from "./dialog";
import Translator from "../../utils/translator";
import template from "./durationSelect.tmpl";

interface IDurationSelectOptions extends Backbone.ViewOptions<Backbone.Model> {
    translator: Translator;
    title?: string;
    duration?: number;
    validate?: (duration: number) => boolean;
    selectCallback: (duration: number) => void;
    resetCallback?: () => void;
    isUserNightMode: boolean;
    isEwd?: boolean;
}

export default class DurationSelect extends Backbone.NativeView<Backbone.Model> {
    private readonly selectCallback;
    private readonly resetCallback;
    private readonly validate;
    private readonly initialDuration: number;
    private readonly translator: Translator;
    private readonly title: string | undefined;
    private readonly isUserNightMode: boolean;
    private readonly isEwd: boolean;
    private dialog: Dialog | null;

    constructor(options: IDurationSelectOptions) {
        super(options);
        this.validate = options.validate;
        this.selectCallback = options.selectCallback;
        this.resetCallback = options.resetCallback;
        this.title = options.title;
        this.initialDuration = Math.round(options.duration || 0);
        this.translator = options.translator;
        this.isEwd = options.isEwd || false;
        this.isUserNightMode = options.isUserNightMode || false;
    }

    renderImpl() {
        let hours = Math.floor(this.initialDuration / 60);
        let minutes = this.initialDuration - hours * 60;
        let templateStr = (_.template(template))({
            selectedHour: hours,
            selectedMinute: minutes,
            isEwd: this.isEwd
        });

        let buttons: IDialogButton[] = [{
            id: "duration__done",
            text: "Done",
            callback: () => this.done()
        }];

        if (this.resetCallback) {
            buttons = [{
                id: "duration__reset",
                text: "Reset",
                className: "button_secondary",
                callback: () => this.reset()
            } as IDialogButton].concat(buttons);
        }

        this.dialog = new Dialog("select-duration", `<div class="selectDialogContent">${ templateStr }</div>`, this.translator, this.title || "Choose duration", [buttons], {
            isNightMode: this.isUserNightMode
        });

        return this.dialog.open()
            .then(() => {
                this.dialog?.el && this.setElement(this.dialog.el.querySelector(".selectDialogContent"));
            });
    }

    render(){
        this.renderImpl();
        return this;
    }

    getSelectedDuration() {
        let dialog = <Dialog> this.dialog;
        let hours = parseInt((<HTMLInputElement>dialog.el?.querySelector(".duration__hours")).value, 10);
        let minutes = parseInt((<HTMLInputElement>dialog.el?.querySelector(".duration__minutes")).value, 10);

        return hours * 60 + minutes;
    }

    setDuration(duration) {
        let hours = Math.floor(duration / 60);
        let minutes = (duration - hours * 60);
        let hoursString = `${ hours }`;
        let minutesString = `${ minutes }`;
        let dialog = <Dialog> this.dialog;

        if (hoursString.length === 1) {
            hoursString = `0${ hoursString }`;
        }

        if (minutesString.length === 1) {
            minutesString = `0${ minutesString }`;
        }

        (<HTMLInputElement>dialog.el?.querySelector(".duration__hours")).value = hoursString;
        (<HTMLInputElement>dialog.el?.querySelector(".duration__minutes")).value = minutesString;
    }

    remove() {
        this.dialog && this.dialog.remove();
        Backbone.View.prototype.remove.call(this);
        return this;
    }

    private done() {
        let duration = this.getSelectedDuration();
        let isValid = true;

        if (this.validate) {
            isValid = this.validate(duration);
        }

        if (isValid) {
            this.selectCallback && this.selectCallback(duration);
            this.dialog && this.dialog.remove();
        }
    }

    private reset() {
        this.resetCallback && this.resetCallback();
        this.dialog && this.dialog.remove();
    }
}